import { ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';

/**
 * Composable for managing filters with URL query param synchronization
 * Handles filter state, URL updates, and filter application logic
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.defaultFilters - Default filter values
 * @param {Function} options.onFilterChange - Callback when filters change
 * @returns {Object} Filter state and methods
 */
export function useFilters(options = {}) {
  const {
    defaultFilters = {},
    onFilterChange = null
  } = options;

  const router = useRouter();
  const route = useRoute();

  /**
   * Initialize filters from URL query params
   */
  const initializeFiltersFromQuery = () => {
    const queryFilters = {};
    
    // Parse all query params and merge with defaults
    Object.keys(defaultFilters).forEach(key => {
      if (route.query[key] !== undefined) {
        // Handle different types of values
        const value = route.query[key];
        
        // Parse integers (for IDs)
        if (key.includes('Id') || key === 'page') {
          queryFilters[key] = value ? parseInt(value) : null;
        }
        // Keep strings as is
        else {
          queryFilters[key] = value;
        }
      } else {
        queryFilters[key] = defaultFilters[key];
      }
    });

    return queryFilters;
  };

  const filters = ref(initializeFiltersFromQuery());
  const previousFilters = ref({ ...filters.value });

  /**
   * Count active filters (non-default values)
   */
  const activeFiltersCount = computed(() => {
    let count = 0;
    Object.keys(filters.value).forEach(key => {
      const value = filters.value[key];
      const defaultValue = defaultFilters[key];
      
      // Skip page and sort keys from count
      if (key === 'page' || key === 'sortBy' || key === 'sort' || key === 'order') return;
      
      // Count if value is different from default and not empty
      if (value && value !== defaultValue && value !== '' && value !== null) {
        count++;
      }
    });
    return count;
  });

  /**
   * Check if filters are currently active
   */
  const hasActiveFilters = computed(() => activeFiltersCount.value > 0);

  /**
   * Update URL query params to match current filters
   */
  const updateUrlParams = () => {
    const query = {};
    
    Object.keys(filters.value).forEach(key => {
      const value = filters.value[key];
      const defaultValue = defaultFilters[key];
      
      // Only include non-default values in URL
      if (value && value !== defaultValue && value !== '' && value !== null) {
        query[key] = value;
      }
    });

    // Update router without triggering navigation
    router.replace({ query });
  };

  /**
   * Apply filters (update URL and trigger callback)
   */
  const applyFilters = () => {
    updateUrlParams();
    if (onFilterChange) {
      onFilterChange(filters.value, previousFilters.value);
    }
    previousFilters.value = { ...filters.value };
  };

  /**
   * Clear all filters to default values
   * Preserves companyId to avoid resetting the top-level company selector
   */
  const clearFilters = () => {
    const preservedCompanyId = filters.value.companyId; // Preserve company selection
    filters.value = { ...defaultFilters };
    filters.value.companyId = preservedCompanyId; // Restore company selection
    applyFilters();
  };

  /**
   * Set a single filter value
   * @param {string} key - Filter key
   * @param {any} value - Filter value
   */
  const setFilter = (key, value) => {
    if (filters.value.hasOwnProperty(key)) {
      filters.value[key] = value;
    }
  };

  /**
   * Set multiple filters at once
   * @param {Object} newFilters - Object with filter key-value pairs
   */
  const setFilters = (newFilters) => {
    Object.keys(newFilters).forEach(key => {
      setFilter(key, newFilters[key]);
    });
  };

  /**
   * Get current filter values
   * @returns {Object} Current filters
   */
  const getFilters = () => ({ ...filters.value });

  /**
   * Get filters formatted for API request
   * Converts filter keys to API parameter names
   * @returns {Object} API-ready filter params
   */
  const getApiParams = () => {
    const params = {};
    
    Object.keys(filters.value).forEach(key => {
      const value = filters.value[key];
      if (value !== null && value !== '' && value !== undefined) {
        // Convert camelCase to snake_case for API
        const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        params[apiKey] = value;
      }
    });

    return params;
  };

  /**
   * Sync filters when URL query changes (browser back/forward)
   */
  watch(() => route.query, (newQuery) => {
    const oldFilters = { ...filters.value };
    filters.value = initializeFiltersFromQuery();
    if (onFilterChange) {
      onFilterChange(filters.value, oldFilters);
    }
    previousFilters.value = { ...filters.value };
  }, { deep: true });

  return {
    // State
    filters,
    
    // Computed
    activeFiltersCount,
    hasActiveFilters,
    
    // Methods
    applyFilters,
    clearFilters,
    setFilter,
    setFilters,
    getFilters,
    getApiParams,
    updateUrlParams
  };
}
